<script>
	import thumb_eJH1DvtWnHQ from '$lib/Components/Youtube/images/eJH1DvtWnHQ.jpg?w=610';

	import Bubble from '$lib/Components/Layout/Bubble/Bubble.svelte';
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import Youtube from '$lib/Components/Youtube/Youtube.svelte';
	import GetAFreeEstimate from '$lib/PageSections/GetAFreeEstimate/GetAFreeEstimate.svelte';
	import Reviews from '$lib/PageSections/Reviews/Reviews.svelte';

	import homeOfficeWithLargeWindow from './images/homeOfficeWithLargeWindow.jpg';
	import manAndChildInHomeOffice from './images/manAndChildInHomeOffice.jpg';
	import trafficOutsideApartmentWindow from './images/trafficOutsideApartmentWindow.jpg';
	import womanInstallingWindowInsert from './images/womanInstallingWindowInsert.jpg';
</script>

<Section>
	<p>
		COVID-19 forced people to work from home. As the initial shock wore off and
		the dust began to settle, many of us found ourselves in our new work areas
		asking: “How do I soundproof a home office?”
	</p>
	<p>
		Here we’ll share with you the best tips and a video guide on how to
		soundproof a home office. When setting up your home office, consider noises
		coming from both outside of and inside your home. We cover both types here
		to make sure your office is soundproof and a comfortable place to work.
	</p>
	<Image
		alt="home office with door and large window with work space and various noise-absorbing decorations"
		src={homeOfficeWithLargeWindow}
	/>
</Section>

<Section>
	<h2>Tips for a Soundproof Home Office</h2>
	<Bubble>
		<h3 class="font-bold text-indow-grey-dark! capitalize">
			How to Soundproof an Office
		</h3>
		<ul>
			<li>
				<p class="text-left">
					Create a dedicated work space with a door that closes
				</p>
			</li>
			<li>
				<p class="text-left">
					Determine where the sound is coming from: the source of the sound is
					important to help you determine if you should be targeting sound
					absorption inside the office, or sound reduction outside of the office
				</p>
			</li>
			<li>
				<p class="text-left">How you can absorb inside noise in an office:</p>
				<ul>
					<li>Adding an area rug and plush furniture</li>
					<li>Wall art or acoustic panels</li>
					<li>Door snakes</li>
				</ul>
			</li>
			<li>
				<p class="text-left">How you can reduce outside noise in an office:</p>
				<ul>
					<li>
						<p class="text-left">
							Sealing air leaks with caulk or weather stripping is a fast, cheap
							way to soundproof
						</p>
					</li>
					<li>
						<p class="text-left">
							Adding window inserts is a great way to help your underperforming
							windows block sound
						</p>
					</li>
				</ul>
			</li>
		</ul>
	</Bubble>
	<p>
		Every “working from home tips” article discusses the technology you need to
		complete your work and stay connected. They warn of distractions, suggesting
		you keep them away. But studies show that <a
			href="https://www.workdesign.com/2019/05/good-office-design-includes-soundproofing-for-productivity"
			target="_blank"
			rel="external">soundproofing is directly linked to productivity</a
		>. As is creative thinking, and health. Aside from that, have you
		encountered a work call interrupted by a child’s scream yet? You will. Make
		sure it’s not yours.
	</p>
</Section>

<Section>
	<h2>Watch Tips on How to Soundproof a Home Office</h2>
	<Youtube posterImageSrc={thumb_eJH1DvtWnHQ} videoId="eJH1DvtWnHQ" />
</Section>

<Section>
	<h2>Make Yourself at Home in Your Dedicated Workspace</h2>
	<p>
		A dedicated work space will make you feel collected, calm, and ready to
		work. All you really need is a good chair, a flat surface, and good
		lighting. Ideally, you also have a door to close. This also gives you the
		opportunity to soundproof your home office.
	</p>
	<p>
		There are two types of soundproofing: reduction and absorption. Noise coming
		from outside your home needs sound reduction, which reduces sound coming
		into your home. Sound absorption reduces echo by absorbing sounds created
		inside your home.
	</p>
</Section>

<Section>
	<h2>Office Soundproofing from Noise Inside Your House</h2>
	<Image
		alt="man & child in home office: stacked artwork against the wall helps absorb noise from adjacent rooms"
		src={manAndChildInHomeOffice}
	/>
	<p>
		It’s safe to say that the sources of noise inside your home, such as a
		puppy, or partner on their own conference call, are far more pleasant to our
		ears and hearts than traffic or construction from the outside. Nonetheless,
		it’s important to soundproof your office against these noises. Fortunately,
		soundproofing with noise reduction materials is easy to do, and the
		solutions are cost-effective!
	</p>
	<p>
		<strong>Use an area rug</strong>. It doesn’t just make you look classy, it
		also helps soundproof your home office. Adding thick materials, like rugs,
		plush furniture, wall art, or even acoustic panels help keep noise in the
		rest of your home from disturbing your dedicated work space.
		<a
			href="https://www.etsy.com/market/door_snake"
			target="_blank"
			rel="external">Door snakes</a
		> and door sweeps also keep small noises from swooping through the air between
		your office door and floor. There are plenty of stylish sound-blocking office
		dividers to choose from, and they’re not only good options for an open plan office.
		Placing these next to walls helps reduce noise coming in from other rooms.
	</p>
	<p>
		<strong>Dampen equipment that vibrates</strong> along with passing music or
		noisy neighbors. This article from Popular Mechanics outlines everything you
		need to secure (vibration-wise) or build for
		<a
			href="https://www.popularmechanics.com/home/interior-projects/how-to/g2470/soundproofing-a-room"
			target="_blank"
			rel="external">soundproofing a room</a
		>. These tips are especially helpful for anyone who has to record audio or
		video at home.
	</p>
</Section>

<Section>
	<h2>Soundproofing Home Office to Outside Noise</h2>
	<Image
		alt="traffic outside of apartment window: outside noise needs sound-blocking items such as window inserts"
		src={trafficOutsideApartmentWindow}
	/>
	<p>
		<strong>Check for any air leaks around the room</strong>. Sound travels by
		air, and you can fix most by adding caulk and weatherstripping. Adding wall
		insulation is always helpful, but a big project.
	</p>
	<p>
		<strong>Add office window soundproofing</strong>. The biggest reduction in
		outside noise with the best cost efficiency is
		<a href="/solutions/window-noise-reduction">window inserts</a>. They allow
		you to block outside noise by installing them in your existing windows
		(rather than replacing your windows or opening your wall for insulation).
		Indow inserts block up to 70% of outside noise as soon as you press them
		into place.
	</p>
	<p>
		Extra bonus: your home office still has plenty of natural light (as opposed
		to using sound-blocking curtains) and they help with temperature control to
		keep you comfortable.
	</p>
</Section>

<Section>
	<h2>How do Soundproofing Windows Work?</h2>
	<p>
		Window inserts are made of the best sound absorbing materials. They put an
		extra layer between you and the noise. Indow uses acrylic because it absorbs
		sound better than glass. The acrylic is surrounded by compression tubing to
		create a seal in your window frame. This creates a dead air space between
		the acrylic panel and your window, which also helps stop noise from entering
		your space.
	</p>
	<Image
		alt="woman next to large bookshelf installing Indow window insert: sound-absorbing vs sound-blocking"
		src={womanInstallingWindowInsert}
	/>
	<p>
		Sound-blocking curtains also reduce noise, but not as much as window inserts
		because they do not create a seal, and they also make your home office very
		dark. Natural light helps our mood, eases stress, <a
			href="https://journals.lww.com/jbisrir/fulltext/2010/08161/the_effects_of_exposure_to_natural_light_in_the.5.aspx"
			target="_blank"
			rel="external">and increases productivity</a
		>.
	</p>
	<p>
		Adding window inserts is like adding another pane of glass to your window,
		but with a better sound absorbing materials and an extra large gap between
		them. Plus, you can remove it at any time with no damage done to your window
		frames.
	</p>
</Section>

<Section>
	<h2>Soundproof Your Office to Help Existing Systems</h2>
	<p>
		Indow has always relied on RingCentral to let sick coworkers or distant
		partners join in on important meetings. Now that we’re all working from our
		homes, that convenience has turned into a daily need to keep business
		functioning. No matter how much <a
			href="https://www.ringcentral.com/blog/enabling-effective-communication-and-collaboration-during-covid-19-for-distributed-and-mobile-organizations"
			target="_blank"
			rel="external">RingCentral adjusts to the times</a
		>, if our meetings are stopped every few minutes by barking dogs, neighbors,
		or even our partners’ meeting in the other room, we don’t get much done.
		Soundproofing for remote meetings is essential – there’s only so much the
		mute button can handle.
	</p>
	<p>
		It’s time to prepare our own spaces for the long haul. Chances are, our home
		office is going to remain our dedicated work space for a while. We can’t
		control how loud our neighbors are, but we can control our day to day
		comfort and how we manage our space. If you learn how to soundproof a home
		office, you are controlling your environment and choosing a healthier work
		space. We give you permission to elect yourself employee of the month.
	</p>
	<p>
		Learn more about how to soundproof your home office with <a
			href="/products/acoustic-window-inserts">window inserts</a
		>.
	</p>
</Section>

<GetAFreeEstimate title="Let Us Help You" />
<Reviews />
